import { UserCircle, Calendar, Award, User } from 'lucide-react';
import type { Client } from '../services/api';

interface ClientCardProps {
  client: Client;
  onAssignPlan: () => void;
  onViewProfile: () => void;
}

export function ClientCard({ client, onAssignPlan, onViewProfile }: ClientCardProps) {
  const subscription = client.subscription;
  const profile = client.profile;

  const tierColors = {
    free: 'bg-gray-100 text-gray-800 border-gray-300',
    standard: 'bg-blue-100 text-blue-800 border-blue-300',
    premium: 'bg-purple-100 text-purple-800 border-purple-300',
  };

  const statusColors = {
    active: 'bg-green-100 text-green-800 border-green-300',
    expired: 'bg-red-100 text-red-800 border-red-300',
    cancelled: 'bg-orange-100 text-orange-800 border-orange-300',
    grace_period: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpired = subscription?.status === 'expired' || !subscription;

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {client.profile_photo_url ? (
              <img
                src={client.profile_photo_url}
                alt={`${client.first_name} ${client.last_name}`}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                <UserCircle className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">
                {client.first_name} {client.last_name}
              </h3>
              <p className="text-sm text-gray-600">{client.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Status */}
      <div className="p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">Subscription</span>
          {subscription && (
            <span
              className={`text-xs px-2 py-1 rounded-full border ${
                statusColors[subscription.status]
              }`}
            >
              {subscription.status}
            </span>
          )}
        </div>
        {subscription ? (
          <div className="space-y-1">
            <div
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                tierColors[subscription.tier]
              }`}
            >
              <Award className="w-3 h-3" />
              {subscription.tier}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Calendar className="w-3 h-3" />
              Expires: {formatDate(subscription.expires_at)}
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-500">No active subscription</p>
        )}
      </div>

      {/* Client Profile Info */}
      {profile && (
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs font-medium text-gray-600 mb-2">Profile</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Age:</span>
              <span className="ml-1 text-gray-900">{profile.age}</span>
            </div>
            <div>
              <span className="text-gray-500">Sex:</span>
              <span className="ml-1 text-gray-900 capitalize">{profile.sex}</span>
            </div>
            <div>
              <span className="text-gray-500">Weight:</span>
              <span className="ml-1 text-gray-900">
                {profile.weight} {profile.weight_unit}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Height:</span>
              <span className="ml-1 text-gray-900">
                {profile.height} {profile.height_unit}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Assigned Plans */}
      <div className="p-4 border-t border-gray-200">
        <div className="text-xs font-medium text-gray-600 mb-2">Assigned Plans</div>
        {client.assigned_plans && client.assigned_plans.length > 0 ? (
          <div className="space-y-1">
            {client.assigned_plans.map((plan) => (
              <div
                key={plan.id}
                className="text-xs text-gray-700 bg-gray-50 px-2 py-1 rounded"
              >
                {plan.title}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500">No plans assigned</p>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 flex gap-2">
        <button
          onClick={onAssignPlan}
          disabled={isExpired}
          className={`flex-1 px-3 py-2 text-sm font-medium rounded transition-colors ${
            isExpired
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
          title={isExpired ? 'Client needs active subscription' : 'Assign a plan'}
        >
          Assign Plan
        </button>
        {profile && (
          <button
            onClick={onViewProfile}
            className="flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
          >
            <User className="w-4 h-4" />
            Profile
          </button>
        )}
      </div>
    </div>
  );
}
